import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { initGlobalKbNav } from "@/lib/keyboardNav";
import { menuKeyOf, isMatrixDrivenRole } from "@/lib/menuKeys";
import { api } from "@/lib/api";
import MinimizedTaskbar from "@/components/MinimizedTaskbar";
import { SyncIndicator } from "@/components/SyncIndicator";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, Bike, Package, ClipboardList,
  Receipt, Sparkles, LogOut, Wrench, User as UserIcon,
  Bell, QrCode, Tag, Package2, Calendar, Boxes, ArrowLeftRight,
  ChevronDown, Building2, FolderTree, Layers,
  IndianRupee, Truck, Hash, Wallet, BookOpen, Landmark,
  FileSpreadsheet, TrendingUp, BookText, HeartHandshake, Star, Megaphone,
  ShieldCheck, UserCog, Shield, Lock, Warehouse, FileText, MapPin, Navigation,
  Undo2, PackageMinus, Search, Briefcase, AlertTriangle, TrendingDown,
  Printer, Languages, DatabaseBackup, Server,
} from "lucide-react";

// Workshop group (order fixed by spec)
const workshop = [
  { to: "/bookings",  label: "Booking",   icon: Calendar,      roles: ["owner", "manager", "receptionist"] },
  { to: "/vehicles",  label: "Vehicle",   icon: Bike,          roles: ["owner", "manager", "cashier", "receptionist", "mechanic"] },
  { to: "/jobcards",  label: "Job Card",  icon: ClipboardList, roles: ["owner", "manager", "cashier", "receptionist", "mechanic"] },
  { to: "/estimates", label: "Estimate", icon: FileSpreadsheet, roles: ["owner", "manager", "cashier", "receptionist"] },
  { to: "/challans",  label: "Challan",  icon: FileText,       roles: ["owner", "manager", "cashier", "receptionist"] },
  { to: "/invoices",  label: "Invoices",  icon: Receipt,       roles: ["owner", "manager", "cashier", "receptionist"] },
  { to: "/jobrates",  label: "Rate Card", icon: Tag,           roles: ["owner", "manager"] },
  { to: "/packages",  label: "Package",   icon: Package2,      roles: ["owner", "manager", "cashier", "receptionist"] },
  { to: "/reminders", label: "Reminders", icon: Bell,          roles: ["owner", "manager", "receptionist"] },
];

// Inventory group (order fixed by spec)
const inventory = [
  { to: "/items",              label: "Item Master",           icon: Boxes,       roles: ["owner", "manager"] },
  { to: "/companies",          label: "Company Master",        icon: Building2,   roles: ["owner", "manager"] },
  { to: "/categories",         label: "Category Master",       icon: FolderTree,  roles: ["owner", "manager"] },
  { to: "/groups",             label: "Group Master",          icon: Layers,      roles: ["owner", "manager"] },
  { to: "/rates",              label: "Rate Master",           icon: IndianRupee, roles: ["owner", "manager"] },
  { to: "/supplier-companies", label: "Supplier Wise Company", icon: Truck,       roles: ["owner", "manager"] },
  { to: "/hsn",                label: "HSN Code Master",       icon: Hash,        roles: ["owner", "manager"] },
  { to: "/stores",             label: "Store Master",          icon: Warehouse,   roles: ["owner", "manager"] },
  { to: "/sectors",            label: "Sector Master",         icon: MapPin,      roles: ["owner", "manager"] },
  { to: "/warranties",         label: "Warranty Master",       icon: Shield,      roles: ["owner", "manager", "cashier", "receptionist"] },
  { to: "/labels",             label: "Labels",                icon: QrCode,      roles: ["owner", "manager", "cashier", "receptionist"] },
];

// Accounts & Finance group (Owner + Manager; P&L Owner-only)
const finance = [
  { to: "/day-book",         label: "Day Book",         icon: BookOpen,         roles: ["owner", "manager"] },
  { to: "/cash-book",        label: "Cash Book",        icon: Wallet,           roles: ["owner", "manager"] },
  { to: "/bank-book",        label: "Bank Book",        icon: Landmark,         roles: ["owner", "manager"] },
  { to: "/ledger",            label: "Ledger",           icon: BookText,         roles: ["owner", "manager"] },
  { to: "/supplier-master",  label: "Supplier Master",  icon: Truck,            roles: ["owner", "manager"] },
  { to: "/debtor-master",    label: "Debtor Master",    icon: UserCog,          roles: ["owner", "manager"] },
  { to: "/expenses",         label: "Expenses",         icon: Wallet,           roles: ["owner", "manager"] },
  { to: "/vouchers",         label: "Vouchers",         icon: BookText,         roles: ["owner", "manager"] },
  { to: "/gst-reports",      label: "GST Reports",      icon: FileSpreadsheet,  roles: ["owner", "manager"] },
  { to: "/profit-loss",      label: "Profit & Loss",    icon: TrendingUp,       roles: ["owner"] },
];

const txnMasterChildren = [
  { to: "/transactions?type=retail_sale",        label: "Retail Sale",        icon: Receipt,       roles: ["owner", "manager", "cashier", "receptionist"], sub: true },
  { to: "/transactions?type=wholesale_sale",     label: "Wholesale Sale",     icon: Receipt,       roles: ["owner", "manager", "cashier", "receptionist"], sub: true },
  { to: "/transactions?type=wholesale_purchase", label: "Wholesale Purchase", icon: Package,       roles: ["owner", "manager", "cashier", "receptionist"], sub: true },
  { to: "/transactions?type=retail_purchase",    label: "Reorder",            icon: Package,       roles: ["owner", "manager", "cashier", "receptionist"], sub: true },
  { to: "/transactions?type=purchase_order",     label: "Purchase Order",     icon: ClipboardList, roles: ["owner", "manager", "cashier", "receptionist"], sub: true },
  { to: "/transactions?type=scrap_issue",        label: "Scrap Issue",        icon: PackageMinus,  roles: ["owner", "manager", "cashier", "receptionist"], sub: true },
  { to: "/transactions?type=scrap_receive",      label: "Scrap Receive",      icon: Boxes,         roles: ["owner", "manager", "cashier", "receptionist"], sub: true },
  { to: "/sale-return",                          label: "Sale Return",        icon: Undo2,         roles: ["owner", "manager", "accountant"], sub: true },
  { to: "/purchase-return",                      label: "Purchase Return",    icon: PackageMinus,  roles: ["owner", "manager", "accountant"], sub: true },
];

const txnGroup = [
  { label: "Transaction Master", icon: ArrowLeftRight, roles: ["owner", "manager", "cashier", "receptionist", "accountant"], sub: true, children: txnMasterChildren },
  { to: "/item-register", label: "Transaction Register", i18n: "nav.item_register", icon: FileText,       roles: ["owner", "manager", "cashier", "receptionist"], sub: true },
];

// Stocks group — nested "Stock Data" sub-menu
const stockDataChildren = [
  { to: "/stocks/all",         label: "All Stock",         icon: Package,        roles: ["owner", "manager", "cashier", "receptionist", "mechanic"], sub: true },
  { to: "/stocks/store-wise",  label: "Store Wise Stock",  icon: Warehouse,      roles: ["owner", "manager", "cashier", "receptionist", "mechanic"], sub: true },
  { to: "/stocks/batches",     label: "Batch Wise Stock",  icon: Layers,         roles: ["owner", "manager", "cashier", "receptionist", "mechanic"], sub: true },
  { to: "/stocks/batch-opening", label: "Batch Wise Stock Opening", icon: Package2, roles: ["owner", "manager", "cashier", "receptionist", "mechanic"], sub: true },
  { to: "/stocks/transfer",    label: "Stock Transfer",    icon: ArrowLeftRight, roles: ["owner", "manager"], sub: true },
];
const stocksGroup = [
  { label: "Stock Data", icon: Package, roles: ["owner", "manager", "cashier", "receptionist", "mechanic"], sub: true, children: stockDataChildren },
  { to: "/stocks/stock-status", label: "Stock Status", icon: AlertTriangle, roles: ["owner", "manager", "cashier", "receptionist", "mechanic"], sub: true },
];

// HRMS group
const hrmsMenu = [
  { to: "/hr/employees",    label: "Employee Master",   icon: Users,      roles: ["owner", "manager"] },
  { to: "/hr/attendance",   label: "Attendance",        icon: Calendar,   roles: ["owner", "manager"] },
  { to: "/hr/leaves",       label: "Leave Management",  icon: FileText,   roles: ["owner", "manager"] },
  { to: "/hr/payroll",      label: "Payroll",           icon: Wallet,     roles: ["owner", "manager"] },
  { to: "/hr/salary-slip",  label: "Salary Slip",       icon: FileText,   roles: ["owner", "manager"] },
  { to: "/hr/departments",  label: "Department Master", icon: Building2,  roles: ["owner", "manager"] },
  { to: "/hr/designations", label: "Designation Master", icon: Briefcase, roles: ["owner", "manager"] },
  { to: "/hr/geo-attendance", label: "Geo Attendance", icon: MapPin, roles: ["owner", "manager", "mechanic", "cashier", "receptionist"] },
  { to: "/hr/geo-fence", label: "Geo Fence Config", icon: Navigation, roles: ["owner", "manager"] },
];

// Reports group (Owner + Manager)
const reportsMenu = [
  { to: "/reports/sales",     label: "Sales Reports",     icon: TrendingUp,       roles: ["owner", "manager"] },
  { to: "/reports/purchase",  label: "Purchase Reports",  icon: Package,          roles: ["owner", "manager"] },
  { to: "/reports/stock",     label: "Stock Reports",     icon: Boxes,            roles: ["owner", "manager"] },
  { to: "/reports/workshop",  label: "Workshop Reports",  icon: Wrench,           roles: ["owner", "manager"] },
  { to: "/reports/financial", label: "Financial Reports", icon: IndianRupee,      roles: ["owner", "manager"] },
  { to: "/reports/geo-attendance", label: "Geo Attendance Report", icon: MapPin,  roles: ["owner", "manager"] },
  { to: "/gst-reports",       label: "GST Reports",       icon: FileSpreadsheet,  roles: ["owner", "manager"] },
];

const topLevel = [
  { to: "/",             label: "Dashboard",    i18n: "nav.dashboard",   icon: LayoutDashboard, roles: ["owner", "manager", "cashier", "receptionist"] },
  { to: "/customers",    label: "Customers",    i18n: "nav.customers",   icon: Users,           roles: ["owner", "manager", "cashier", "receptionist"] },
  { to: "/insights",     label: "AI Insights",  i18n: "nav.ai_insights", icon: Sparkles,        roles: ["owner", "manager"] },
  { to: "/portal",       label: "My Vehicles",  i18n: "nav.my_vehicles", icon: Bike,            roles: ["customer"] },
];

// Platform Admin sub-menu (nested flyout inside Org & Access dropdown).
// Children are gated by platform_role — same rules as AdminShell.
const platformAdminChildren = [
  { to: "/admin",                   label: "Platform Admin Dashboard",  icon: LayoutDashboard, proles: ["super_admin", "support_admin", "billing_admin"], perm: "pa_dashboard" },
  { to: "/admin/organisation",      label: "Organisation Master",       icon: Building2,       proles: ["super_admin"], perm: "pa_organisation" },
  { to: "/admin/establishments",    label: "Establishment Master",      icon: Warehouse,       proles: ["super_admin"], perm: "pa_establishment" },
  { to: "/admin/org-users",         label: "Organisation User Master",  icon: Users,           proles: ["super_admin"], perm: "pa_org_users" },
  { to: "/admin/backup",            label: "Backup & Restore",          icon: DatabaseBackup,  proles: ["super_admin"], perm: "pa_backup" },
  { to: "/admin/organisation-auth", label: "Organisation Authorisation",icon: ShieldCheck,     proles: ["super_admin", "billing_admin"], perm: "pa_org_auth" },
  { to: "/admin/master-auth",       label: "Master Authorisation",      icon: Boxes,           proles: ["super_admin"], perm: "pa_master_auth" },
  { label: "SaaS Admin User", icon: Users, proles: ["super_admin"], sub: true, children: [
    { to: "/admin/admin-users",       label: "Admin User",                 icon: Users,    proles: ["super_admin"], perm: "pa_admin_users" },
    { to: "/admin/admin-roles",       label: "Admin Role and Permission",  icon: Shield,   proles: ["super_admin"], perm: "pa_admin_roles" },
    { to: "/admin/admin-user-auth",   label: "Authorisation",              icon: ShieldCheck, proles: ["super_admin"], perm: "pa_admin_user_auth" },
  ]},
  { to: "/admin/roles-master",      label: "Role & Permission Master",  icon: Shield,          proles: ["super_admin"], perm: "pa_roles_master" },
  { to: "/admin/password-master",   label: "Password Master",           icon: Lock,            proles: ["super_admin"], perm: "pa_password_master" },
  { to: "/admin/plan-master",       label: "Plan Master",               icon: Briefcase,       proles: ["super_admin", "billing_admin"], perm: "pa_plan_master" },
];

// Org & Access — Organisation and Platform Admin both expand as nested
// vertical sub-menus in the same panel (same behaviour as Stock Data).
const org = [
  { label: "Platform Admin", icon: ShieldCheck, adminOnly: true, sub: true, children: platformAdminChildren },
  { label: "Organisation", icon: Building2, roles: ["owner", "manager"], sub: true, children: [
    { to: "/organisation-profile", label: "Organisation", icon: Building2,   roles: ["owner", "manager"] },
    { to: "/establishment",        label: "Establishment", icon: Warehouse,   roles: ["owner", "manager"] },
    { to: "/establishment/users",  label: "User",          icon: Users,       roles: ["owner", "manager"] },
    { to: "/establishment/roles",  label: "Role & Permission Master", icon: ShieldCheck, roles: ["owner", "manager"] },
  ]},
];

// CRM group
const crm = [
  { to: "/customer-master", label: "Customer Master",     icon: Users,      roles: ["owner", "manager", "cashier", "receptionist", "mechanic"] },
  { to: "/reminders",       label: "Follow-up Reminders", icon: Bell,       roles: ["owner", "manager", "receptionist"] },
  { to: "/feedback",        label: "Feedback",            icon: Star,       roles: ["owner", "manager"] },
  { to: "/campaigns",       label: "Marketing Campaigns", icon: Megaphone,  roles: ["owner", "manager"] },
];

// Settings menu items (extracted so the admin permission tree can mirror them)
const settingsItems = [
  { to: "/settings/financial",    label: "Financial Settings",    i18n: "settings.tab.financial",    icon: IndianRupee },
  { to: "/settings/transaction",  label: "Transaction Settings",  i18n: "settings.tab.transaction",  icon: ArrowLeftRight },
  { label: "Print Settings", icon: Printer, sub: true, children: [
    { to: "/settings/print",           label: "Print Settings",         i18n: "settings.tab.print", icon: Printer },
    { to: "/settings/print-templates", label: "Print Template Master",  icon: Printer },
  ]},
  { label: "Backup & Restore", icon: DatabaseBackup, sub: true, children: [
    { to: "/settings/backup",  label: "Backup",  icon: DatabaseBackup },
    { to: "/settings/restore", label: "Restore", icon: Undo2 },
  ]},
  { to: "/settings/local-server", label: "Shop Server & Sync",    icon: Server,    roles: ["owner"] },
  { to: "/settings/notification", label: "Notification Settings", i18n: "settings.tab.notification", icon: Bell },
  { to: "/settings/language",     label: "Language Settings",     i18n: "settings.tab.language",     icon: Languages },
  { to: "/settings/stock",        label: "Stock Settings",        icon: Warehouse, roles: ["owner", "manager"] },
  { to: "/settings/job-types",    label: "Job Type Master",       icon: Wrench,    roles: ["owner", "manager"] },
];

// ---- Admin permission tree (auto-derived from the nav arrays above) ----
// Single source of truth: any menu/submenu/page added to the navigation arrays
// automatically appears in Admin Role and Permission Master → Section 2.
export const navPermKeyOf = (to) => {
  if (!to) return null;
  const [path, qs] = to.split("?");
  if (path === "/") return "menu_dashboard";
  let slug = path.replace(/^\//, "").replace(/[/-]/g, "_");
  const t = qs && qs.match(/type=([\w]+)/);
  if (t) slug += `_${t[1]}`;
  return `menu_${slug}`;
};
const grpKey = (label) => "grp_" + label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
const toPermNode = (n) => n.children
  ? { key: grpKey(n.label), label: n.label, children: n.children.map(toPermNode) }
  : { key: n.perm || navPermKeyOf(n.to), label: n.label };

// The same page can appear under two menu groups (e.g. /reminders in CRM and
// Workshop, /gst-reports in Accounts and Reports) — identical path = identical
// page = ONE permission row. First occurrence wins; empties groups drop out.
const dedupeTree = (nodes, seen = new Set()) => nodes
  .map(n => n.children ? { ...n, children: dedupeTree(n.children, seen) } : (seen.has(n.key) ? null : (seen.add(n.key), n)))
  .filter(Boolean)
  .filter(n => !n.children || n.children.length > 0);

export const ADMIN_MENU_TREE = dedupeTree([
  toPermNode(topLevel[0]),  // Dashboard
  toPermNode(topLevel[1]),  // Customers
  toPermNode({ label: "CRM", children: crm }),
  toPermNode({ label: "Transactions", children: txnGroup }),
  toPermNode({ label: "Workshop", children: workshop }),
  toPermNode({ label: "Inventory", children: inventory }),
  toPermNode({ label: "Stocks", children: stocksGroup }),
  toPermNode({ label: "Accounts", children: finance }),
  toPermNode({ label: "HRMS", children: hrmsMenu }),
  toPermNode({ label: "Reports", children: reportsMenu }),
  toPermNode(topLevel[2]),  // AI Insights
  toPermNode({ label: "Org & Access", children: org }),
  toPermNode({ label: "Settings", children: settingsItems }),
]);

const navItemActive = (location, it) => it.children
  ? it.children.some(c => navItemActive(location, c))
  : it.to === "/"
    ? location.pathname === "/"
    : it.to.includes("?")
      ? (location.pathname + location.search) === it.to
      : location.pathname.startsWith(it.to);

const slug = (label) => label.toLowerCase().replace(/\s+/g, "-");

// Hover zone with close tolerance: trigger + panel act as one continuous hover
// area; closing is delayed ~130ms so micro-movements don't collapse the menu.
function useHoverZone(delay = 130) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const clear = () => clearTimeout(timer.current);
  const openZone = () => { clear(); setOpen(true); };
  const closeZone = () => { clear(); timer.current = setTimeout(() => setOpen(false), delay); };
  const closeNow = () => { clear(); setOpen(false); };
  const toggle = () => { clear(); setOpen(o => !o); };
  useEffect(() => clear, []);
  return { open, openZone, closeZone, closeNow, toggle };
}

// Plain top-level navbar link (Dashboard, Customers, Settings, …)
function TopLink({ item }) {
  const { t } = useI18n();
  const location = useLocation();
  const active = navItemActive(location, item);
  return (
    <NavLink
      to={item.to} end={item.to === "/"}
      data-testid={`nav-${slug(item.label)}`}
      className={`flex items-center gap-1 px-1 py-2 text-xs min-[1440px]:text-sm rounded-sm whitespace-nowrap transition-colors ${
        active ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
      }`}
    >
      <item.icon className="w-3.5 h-3.5" />
      <span className="uppercase tracking-wide font-semibold">{item.i18n ? t(item.i18n, item.label) : item.label}</span>
    </NavLink>
  );
}

// Link inside a dropdown panel
function DropLink({ item }) {
  const { t } = useI18n();
  const location = useLocation();
  const active = navItemActive(location, item);
  return (
    <NavLink
      to={item.to}
      data-testid={`nav-${slug(item.label)}`}
      className={`flex items-center gap-2.5 px-3 py-2 text-xs rounded-sm transition-colors ${
        active ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
      }`}
    >
      <item.icon className="w-3.5 h-3.5" />
      <span className="uppercase tracking-wide font-semibold">{item.i18n ? t(item.i18n, item.label) : item.label}</span>
    </NavLink>
  );
}

// Third-level nested sub-menu (e.g. Platform Admin → SaaS Admin User → Admin User).
// Self-managed open state with the same hover-intent dwell as TopSubMenu.
function TopSubSubMenu({ item }) {
  const location = useLocation();
  const active = item.children.some(c => navItemActive(location, c));
  const testId = `nav-${slug(item.label)}-toggle`;
  const Icon = item.icon;
  const [open, setOpen] = useState(false);
  const intent = useRef(null);
  useEffect(() => () => clearTimeout(intent.current), []);
  const intentOpen = () => { clearTimeout(intent.current); intent.current = setTimeout(() => setOpen(true), SUB_MENU_INTENT_MS); };
  const cancelIntent = () => clearTimeout(intent.current);
  const toggle = () => { clearTimeout(intent.current); setOpen(o => !o); };
  return (
    <div onMouseEnter={intentOpen} onMouseLeave={cancelIntent}>
      <button
        type="button"
        data-testid={testId}
        onClick={toggle}
        className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs rounded-sm transition-colors ${
          active ? "text-foreground bg-secondary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
        }`}
      >
        <Icon className="w-3.5 h-3.5"/>
        <span className="uppercase tracking-wide font-semibold flex-1 text-left">{item.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div data-sub-menu={testId} className="ml-4 pl-2 border-l border-border/60 space-y-0.5 py-0.5">
          {item.children.map(c => <DropLink key={c.to} item={c} />)}
        </div>
      )}
    </div>
  );
}

// Nested sub-menu (Transaction Master / Stock Data) — expands as an indented
// vertical list within the same dropdown panel. Hover-intent: the sub-menu only
// becomes active after the cursor dwells on it ~180ms, so diagonal movement
// across sibling items toward the open panel never switches it. The parent
// dropdown owns the active sub-menu, so an open panel stays open while the
// cursor is anywhere inside the parent dropdown, and only closes when the
// cursor leaves the whole dropdown (or another item is dwelled on / clicked).
const SUB_MENU_INTENT_MS = 180;
function TopSubMenu({ item, activeSub, onSubChange }) {
  const location = useLocation();
  const active = item.children.some(c => navItemActive(location, c));
  const testId = `nav-${slug(item.label)}-toggle`;
  const Icon = item.icon;
  const open = activeSub === testId;
  const intent = useRef(null);
  useEffect(() => () => clearTimeout(intent.current), []);
  const intentOpen = () => { clearTimeout(intent.current); intent.current = setTimeout(() => onSubChange(testId), SUB_MENU_INTENT_MS); };
  const cancelIntent = () => clearTimeout(intent.current);
  const toggle = () => { clearTimeout(intent.current); onSubChange(open ? null : testId); };
  return (
    <div onMouseEnter={intentOpen} onMouseLeave={cancelIntent}>
      <button
        type="button"
        data-testid={testId}
        onClick={toggle}
        className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs rounded-sm transition-colors ${
          active ? "text-foreground bg-secondary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
        }`}
      >
        <Icon className="w-3.5 h-3.5"/>
        <span className="uppercase tracking-wide font-semibold flex-1 text-left">{item.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div data-sub-menu={testId} className="ml-4 pl-2 border-l border-border/60 space-y-0.5 py-0.5">
          {item.children.map(c => c.children
            ? <TopSubSubMenu key={c.label} item={c} />
            : <DropLink key={c.to} item={c} />)}
        </div>
      )}
    </div>
  );
}

// Top-level dropdown menu (Workshop, Inventory, …) — opens on hover or click
function TopMenu({ label, icon: Icon, testId, items }) {
  const { open, openZone, closeZone, closeNow, toggle } = useHoverZone();
  const [activeSub, setActiveSub] = useState(null);
  const ref = useRef(null);
  const location = useLocation();
  const active = items.some(it => navItemActive(location, it));

  useEffect(() => { closeNow(); }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!open) setActiveSub(null); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) closeNow(); };
    const onKey = (e) => { if (e.key === "Escape") closeNow(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (items.length === 0) return null;
  return (
    <div ref={ref} className="relative" onMouseEnter={openZone} onMouseLeave={closeZone}>
      <button
        type="button"
        data-testid={testId}
        onClick={toggle}
        className={`flex items-center gap-1 px-1 py-2 text-xs min-[1440px]:text-sm rounded-sm whitespace-nowrap transition-colors ${
          active ? "text-foreground bg-secondary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
        }`}
      >
        <Icon className="w-3.5 h-3.5"/>
        <span className="uppercase tracking-wide font-semibold">{label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className={`absolute ${testId === "nav-settings-toggle" ? "right-0" : "left-0"} top-full pt-1 z-50`}>
        <div data-sub-menu={testId} className="min-w-56 bg-card border border-border rounded-sm shadow-xl py-1 max-h-[70vh] overflow-y-auto">
          {items.map(item => item.children
            ? <TopSubMenu key={item.label} item={item} activeSub={activeSub} onSubChange={setActiveSub} />
            : <DropLink key={item.to} item={item} />)}
        </div>
        </div>
      )}
    </div>
  );
}

// Line-1 widgets: organisation name, global search, FY selector, bell, user menu
function OrgName() {
  const [name, setName] = useState("");
  useEffect(() => { api.get("/organisation").then(r => setName(r.data?.[0]?.business_name || "")).catch(() => {}); }, []);
  if (!name) return null;
  return <div data-testid="org-name" className="hidden md:block font-display font-bold text-sm uppercase tracking-wider truncate max-w-64">{name}</div>;
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q.trim())}`).then(r => { setRes(r.data); setOpen(true); }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);
  const go = (to) => { setOpen(false); setQ(""); setRes(null); navigate(to); };
  const groups = res ? [
    ["Items", res.items, () => "/items", r => `${r.item_name}${r.part_number ? ` · ${r.part_number}` : ""}`],
    ["Customers", res.customers, () => "/customer-master", r => `${r.name}${r.mobile ? ` · ${r.mobile}` : ""}`],
    ["Vehicles", res.vehicles, () => "/vehicles", r => r.registration],
    ["Suppliers", res.suppliers, () => "/supplier-master", r => r.name],
  ] : [];
  const hasAny = groups.some(([, arr]) => (arr || []).length > 0);
  return (
    <div ref={ref} className="relative flex-1 max-w-72 min-w-0">
      <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"/>
      <input
        data-testid="global-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => res && setOpen(true)}
        placeholder="Search items, customers, vehicles…"
        className="w-full h-8 pl-8 pr-3 rounded-sm bg-secondary border border-border text-xs outline-none focus:border-primary placeholder:text-muted-foreground"
      />
      {open && res && (
        <div data-testid="global-search-results" className="absolute left-0 top-full mt-1 w-full bg-card border border-border rounded-sm shadow-xl z-50 py-1 max-h-80 overflow-y-auto">
          {!hasAny && <div className="px-3 py-2 text-xs text-muted-foreground italic">No matches</div>}
          {groups.map(([label, arr, to, text]) => (arr || []).length > 0 && (
            <div key={label}>
              <div className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
              {arr.map(r => (
                <button key={r.id} type="button" data-testid={`gs-${label.toLowerCase()}-${r.id}`} onClick={() => go(to(r))}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-secondary transition-colors truncate">{text(r)}</button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FYSelector() {
  const now = new Date();
  const fyStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1; // Indian FY starts in April
  const options = [fyStart - 1, fyStart, fyStart + 1].map(y => `FY ${y}-${String(y + 1).slice(2)}`);
  const [fy, setFy] = useState(() => localStorage.getItem("app_fy") || `FY ${fyStart}-${String(fyStart + 1).slice(2)}`);
  return (
    <select
      data-testid="fy-selector"
      value={fy}
      onChange={(e) => { setFy(e.target.value); localStorage.setItem("app_fy", e.target.value); }}
      className="h-8 px-2 rounded-sm bg-secondary border border-border text-xs font-mono text-muted-foreground outline-none focus:border-primary"
    >
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function NotificationBell({ role }) {
  const [alerts, setAlerts] = useState({ lowStock: [], reminders: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/analytics/summary").then(r => {
      const d = r.data || {};
      setAlerts(a => ({ ...a, lowStock: Array.isArray(d.low_stock_items) ? d.low_stock_items : (Array.isArray(d.low_stock) ? d.low_stock : []) }));
    }).catch(() => {});
    if (["owner", "manager", "cashier", "receptionist"].includes(role)) {
      api.get("/reminders").then(r => {
        setAlerts(a => ({ ...a, reminders: (r.data || []).filter(x => x.status !== "dismissed").length }));
      }).catch(() => {});
    }
  }, [role]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  const count = alerts.lowStock.length + alerts.reminders;
  return (
    <div ref={ref} className="relative">
      <button type="button" data-testid="notif-bell" onClick={() => setOpen(o => !o)}
        className="relative w-8 h-8 flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
        <Bell className="w-4 h-4"/>
        {count > 0 && <span data-testid="notif-count" className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-0.5 bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center rounded-sm">{count}</span>}
      </button>
      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 top-full mt-1 w-72 bg-card border border-border rounded-sm shadow-xl z-50 py-1 max-h-80 overflow-y-auto">
          <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-border">Notifications</div>
          {alerts.lowStock.length > 0 && (
            <button type="button" data-testid="notif-low-stock" onClick={() => { setOpen(false); navigate("/stocks/all"); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-secondary transition-colors">
              <span className="text-yellow-500 font-semibold">Low stock:</span> {alerts.lowStock.length} item{alerts.lowStock.length === 1 ? "" : "s"} below reorder level
            </button>
          )}
          {alerts.reminders > 0 && (
            <button type="button" data-testid="notif-reminders" onClick={() => { setOpen(false); navigate("/reminders"); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-secondary transition-colors">
              <span className="text-primary font-semibold">Reminders:</span> {alerts.reminders} open follow-up{alerts.reminders === 1 ? "" : "s"}
            </button>
          )}
          {count === 0 && <div className="px-3 py-3 text-xs text-muted-foreground italic">All clear — no alerts</div>}
        </div>
      )}
    </div>
  );
}

function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button type="button" data-testid="user-profile-btn" onClick={() => setOpen(o => !o)}
        className="w-8 h-8 bg-primary/20 border border-primary/40 flex items-center justify-center rounded-sm hover:bg-primary/30 transition-colors">
        <UserIcon className="w-4 h-4 text-primary"/>
      </button>
      {open && (
        <div data-testid="user-menu" className="absolute right-0 top-full mt-1 w-56 bg-card border border-border rounded-sm shadow-xl z-50 py-1">
          <div className="px-3 py-2 border-b border-border">
            <div className="text-xs font-semibold truncate">{user.name}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{user.role}</div>
          </div>
          <button type="button" data-testid="logout-btn" onClick={onLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-destructive hover:bg-secondary transition-colors">
            <LogOut className="w-3.5 h-3.5"/> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout, orgMenus, adminMenus, adminNavPerms, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { initGlobalKbNav(); }, []);

  // Measure the fixed top bar's actual rendered height so the content area
  // fills exactly the remaining viewport below it (never hidden behind it).
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => setHeaderH(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => { ro.disconnect(); window.removeEventListener("resize", update); };
  }, []);

  // After opening a page (e.g. Enter on a menu item), place the cursor on the
  // first input field, list row or button so keyboard navigation continues
  // without a mouse click.
  useEffect(() => {
    window.__kbReturnTo = null;
    const t = setTimeout(() => {
      if (document.querySelector('[role="dialog"]')) return;
      if (document.querySelector('[data-testid$="-form-page"]')) return; // transaction forms self-focus
      const main = document.querySelector("main");
      if (!main) return;
      const first = main.querySelector('input:not([disabled]):not([type="hidden"]):not([readonly])')
        || main.querySelector('[role="combobox"]')
        || main.querySelector("tbody tr[data-kb-tr]")
        || main.querySelector("button:not([disabled])");
      first?.focus();
    }, 250);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Org-level (SaaS plan) menu gating: nav paths → permission menu keys.
  // orgMenus === null means no org authorisation is configured → everything shows.
  // (PATH_KEY / menuKeyOf live in @/lib/menuKeys, shared with App route guards.)
  // Hardcoded: SaaS Admin sees every menu/submenu regardless of role lists,
  // org menu gating, or any authorisation configuration. (Customer-portal-only
  // items like My Vehicles stay hidden to keep the ERP navbar intact.)
  const isSA = !!user.is_platform_admin && (user.platform_role || "super_admin") === "super_admin";
  const roleOk = (n) => !n.roles || n.roles.includes(user.role)
    || (isSA && !(n.roles.length === 1 && n.roles[0] === "customer"));
  // Matrix-driven roles (mechanic + custom roles from Role & Permission Master):
  // menu visibility follows the saved permission matrix (no View = hidden).
  // Unmapped paths fall back to the static role lists. All other roles unchanged.
  const matrixDriven = isMatrixDrivenRole(user.role);
  const viewOk = (n) => {
    if (!matrixDriven) return true;
    const k = menuKeyOf(n.to);
    return k ? can(k, "view") : roleOk(n);
  };
  // Admin users (non-super): ERP navbar visibility comes from their admin role's
  // Section 2 menu matrix (Admin Role and Permission Master) — no View = hidden.
  // Keys are auto-derived from the item's path (navPermKeyOf). SaaS Admin and
  // regular org users are unaffected.
  const erpOk = (n) => {
    if (isSA || !user.is_platform_admin) return matrixDriven ? viewOk(n) : roleOk(n);
    if (n.children) return true;          // groups derive visibility from their children
    if (n.perm) return true;              // Platform Admin flyout: governed by proleOk + permOk
    if (!adminNavPerms) return false;     // fail closed until role perms load
    const k = navPermKeyOf(n.to);
    return k ? adminNavPerms[k]?.view === true : roleOk(n);
  };
  const orgOk = (item) => {
    if (isSA || !orgMenus) return true;
    const k = menuKeyOf(item.to);
    return k ? orgMenus[k] !== false : true;
  };

  const workshopItems = workshop.filter(n => erpOk(n) && orgOk(n));
  const inventoryItems = inventory.filter(n => erpOk(n) && orgOk(n));
  const financeItems = finance.filter(n => erpOk(n) && orgOk(n));
  const crmItems = crm.filter(n => erpOk(n) && orgOk(n));
  const pRole = user.platform_role || "super_admin";
  // Static proles OR an explicit role/user menu grant (perm key) opens the item —
  // without this, custom admin roles could never reach role-granted platform pages.
  const proleOk = (c) => isSA || !c.proles || c.proles.includes(pRole)
    || !!(c.perm && adminMenus && adminMenus[c.perm]?.view === true);
  // Per-user admin authorisation (View unchecked = hidden). SaaS Admin bypasses
  // (hardcoded full access); null adminMenus (non-platform users) = static proles only.
  const permOk = (c) => isSA || !c.perm || !adminMenus || adminMenus[c.perm]?.view !== false;
  const orgItems = org
    .map(n => n.children ? { ...n, children: n.children
        .map(c => c.children ? { ...c, children: c.children.filter(c2 => proleOk(c2) && permOk(c2)) } : c)
        .filter(c => proleOk(c) && permOk(c) && erpOk(c) && (!c.children || c.children.length > 0)) } : n)
    .filter(n => (n.adminOnly ? user.is_platform_admin : erpOk(n)) && orgOk(n) && (!n.children || n.children.length > 0));
  const reportsItems = reportsMenu.filter(n => erpOk(n) && orgOk(n));
  const hrmsItems = hrmsMenu.filter(n => erpOk(n) && orgOk(n));
  const settingsRoleOk = (n) => isSA || user.is_platform_admin || user.role === "owner" || (n.roles || []).includes(user.role);
  const settingsList = settingsItems
    .map(n => n.children ? { ...n, children: n.children.filter(c => erpOk(c) && settingsRoleOk(c)) } : n)
    .filter(n => erpOk(n) && settingsRoleOk(n) && (!n.children || n.children.length > 0));
  const txnItems = txnGroup
    .map(n => n.children ? { ...n, children: n.children.filter(c => erpOk(c) && orgOk(c)) } : n)
    .filter(n => erpOk(n) && orgOk(n) && (!n.children || n.children.length > 0));
  const stockItems = stocksGroup
    .map(n => n.children ? { ...n, children: n.children.filter(c => erpOk(c) && orgOk(c)) } : n)
    .filter(n => erpOk(n) && orgOk(n) && (!n.children || n.children.length > 0));
  const otherItems = topLevel.filter(n => erpOk(n) && orgOk(n));
  const dashboard = otherItems.find(i => i.to === "/");
  const rest = otherItems.filter(i => i.to !== "/" && i.to !== "/customers");

  // Navbar keyboard nav: arrows walk menu items; Right/Down expands a group
  // (Down steps into its first sub-item); Left/Up/Backspace on the first
  // sub-item collapses the group and refocuses its header; Enter stays native.
  const handleNavKeys = (e) => {
    if (!e.key.startsWith("Arrow") && e.key !== "Backspace") return;
    const t = e.target;
    if (!t || !t.closest) return;
    const nav = e.currentTarget;
    const items = [...nav.querySelectorAll('[data-testid^="nav-"]')].filter(el => el.offsetParent !== null);
    const idx = items.indexOf(t);
    if (idx === -1) return;
    const tid = t.getAttribute("data-testid") || "";
    const isToggle = tid.endsWith("-toggle");
    const subMenu = t.closest("[data-sub-menu]");
    const fwd = e.key === "ArrowRight" || e.key === "ArrowDown";
    const bwd = e.key === "ArrowLeft" || e.key === "ArrowUp" || e.key === "Backspace";

    if (isToggle && fwd) {
      e.preventDefault(); e.stopPropagation();
      const sub = nav.querySelector(`[data-sub-menu="${tid}"]`);
      if (!sub) {
        t.click();
        if (e.key === "ArrowDown") setTimeout(() => nav.querySelector(`[data-sub-menu="${tid}"] [data-testid^="nav-"]`)?.focus(), 90);
      } else if (e.key === "ArrowDown") {
        sub.querySelector('[data-testid^="nav-"]')?.focus();
      }
      return;
    }
    if (subMenu && bwd) {
      if (t === subMenu.querySelector('[data-testid^="nav-"]')) {
        e.preventDefault(); e.stopPropagation();
        const hdr = nav.querySelector(`[data-testid="${subMenu.getAttribute("data-sub-menu")}"]`);
        if (hdr) { hdr.click(); hdr.focus(); }
        return;
      }
    }
    if (isToggle && e.key === "ArrowLeft") {
      const sub = nav.querySelector(`[data-sub-menu="${tid}"]`);
      if (sub) { e.preventDefault(); e.stopPropagation(); t.click(); return; }
    }
    e.preventDefault(); e.stopPropagation();
    const next = items[idx + (fwd ? 1 : -1)];
    if (next) next.focus();
  };

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden bg-background text-foreground" style={{ height: "100dvh", width: "100dvw" }}>
      <header ref={headerRef} className="border-b border-border bg-card sticky top-0 z-40 w-full shrink-0">
        {/* Line 1: logo · organisation name + global search · FY / bell / profile */}
        <div className="flex items-center gap-3 px-4 h-12 border-b border-border/60">
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 bg-primary flex items-center justify-center">
              <Wrench className="w-4 h-4 text-primary-foreground" strokeWidth={2.5}/>
            </div>
            <div>
              <div className="font-display font-black text-lg leading-none uppercase tracking-tight">SynqMoto<span className="text-primary"> ERP</span></div>
              <div className="text-[9px] text-muted-foreground uppercase tracking-[0.2em]">Automobile OS</div>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center gap-4 min-w-0">
            <OrgName/>
            <GlobalSearch/>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <SyncIndicator canOpen={user.role === "owner" || isSA}/>
            <FYSelector/>
            <NotificationBell role={user.role}/>
            <UserMenu user={user} onLogout={async () => { await logout(); navigate("/login"); }}/>
          </div>
        </div>

        {/* Line 2: main navigation menus (wraps to extra rows on narrow screens; header height is measured) */}
        <nav className="flex items-center justify-evenly gap-0 px-2 min-h-11 flex-wrap w-full" onKeyDownCapture={handleNavKeys}>
          {dashboard && <TopLink item={dashboard} />}
          {crmItems.length > 0 && <TopMenu label="CRM" icon={HeartHandshake} testId="nav-crm-toggle" items={crmItems} />}
          {txnItems.length > 0 && <TopMenu label="Transactions" icon={ArrowLeftRight} testId="nav-transactions-toggle" items={txnItems} />}
          {workshopItems.length > 0 && <TopMenu label="Workshop" icon={Wrench} testId="nav-workshop-toggle" items={workshopItems} />}
          {inventoryItems.length > 0 && <TopMenu label="Inventory" icon={Package} testId="nav-inventory-toggle" items={inventoryItems} />}
          {stockItems.length > 0 && <TopMenu label="Stocks" icon={Package} testId="nav-stocks-toggle" items={stockItems} />}
          {financeItems.length > 0 && <TopMenu label="Accounts" icon={IndianRupee} testId="nav-finance-toggle" items={financeItems} />}
          {hrmsItems.length > 0 && <TopMenu label="HRMS" icon={Users} testId="nav-hrms-toggle" items={hrmsItems} />}
          {reportsItems.length > 0 && <TopMenu label="Reports" icon={FileSpreadsheet} testId="nav-reports-toggle" items={reportsItems} />}
          {rest.map(item => <TopLink key={item.to} item={item} />)}
          {orgItems.length > 0 && <TopMenu label="Org & Access" icon={ShieldCheck} testId="nav-org-toggle" items={orgItems} />}
          {settingsList.length > 0 && (user.role === "owner" || user.role === "manager" || isSA || user.is_platform_admin) &&
            <TopMenu label="Settings" icon={ShieldCheck} testId="nav-settings-toggle" items={settingsList} />}
        </nav>
      </header>

      <main className="flex-1 min-h-0 w-full overflow-y-auto overflow-x-hidden" style={headerH ? { height: `calc(100dvh - ${headerH}px)` } : undefined}>
        <div className="pb-16">
          <Outlet />
        </div>
        <MinimizedTaskbar />
      </main>
    </div>
  );
}
